import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { resetPassword } from '../../services/authService';
import AuthShell from '../../components/AuthShell/AuthShell';
import { readStashedToken } from '../../utils/sensitiveUrl';
import styles from '../../components/AuthShell/authForm.module.css';

export default function ResetPassword() {
    const [searchParams] = useSearchParams();
    // Token is scrubbed from the URL at boot (utils/sensitiveUrl.js) so it never reaches analytics.
    const token = readStashedToken('token', '/reset-password') || searchParams.get('token') || '';
    const navigate = useNavigate();

    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const validate = () => {
        const e = {};
        if (!/^.{8,}$/.test(password) || !/[a-zA-Z]/.test(password) || !/\d/.test(password)) {
            e.password = 'Min 8 characters with at least one letter and one number.';
        }
        if (password !== confirm) e.confirm = 'Passwords do not match.';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setLoading(true);
        try {
            await resetPassword(token, password);
            setDone(true);
            setTimeout(() => navigate('/login'), 3000);
        } catch (err) {
            setErrors({ form: err.message || 'Reset failed. The link may have expired.' });
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <AuthShell
                mode="reset"
                title="Invalid link"
                subtitle="This password reset link is invalid or has expired."
                pageTitle="Reset your password · Aluvo"
                compact
                footer={
                    <p className={styles.switchText}>
                        <Link to="/forgot-password" className={styles.link}>Request a new one</Link>
                    </p>
                }
            />
        );
    }

    return (
        <AuthShell
            mode="reset"
            title="Set new password"
            subtitle={done ? undefined : 'Choose a strong password for your Aluvo account.'}
            pageTitle="Reset your password · Aluvo"
            compact
            clarityMask
            footer={done ? (
                <p className={styles.switchText}>
                    <Link to="/login" className={styles.link}>Sign In now</Link>
                </p>
            ) : undefined}
        >
            {done ? (
                <p className={styles.infoMsg} role="status">
                    ✅ Your password has been reset. Redirecting you to sign in…
                </p>
            ) : (
                <form className={styles.form} onSubmit={handleSubmit} noValidate aria-label="Set a new password">
                    <div className={styles.field}>
                        <label htmlFor="reset-password" className={styles.srOnly}>New password</label>
                        <div className={`${styles.passwordWrap} ${errors.password ? styles.inputError : ''}`}>
                            <input
                                id="reset-password"
                                className={styles.passwordInput}
                                type={showPassword ? 'text' : 'password'}
                                placeholder="New password"
                                value={password}
                                onChange={(e) => { setPassword(e.target.value); setErrors(p => ({ ...p, password: undefined })); }}
                                autoComplete="new-password"
                                aria-invalid={errors.password ? 'true' : undefined}
                                aria-describedby={errors.password ? 'reset-password-error' : undefined}
                            />
                            <button
                                type="button"
                                className={styles.eyeBtn}
                                onClick={() => setShowPassword(s => !s)}
                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                                aria-pressed={showPassword}
                            >
                                {showPassword ? '🙈' : '👁️'}
                            </button>
                        </div>
                        {errors.password && (
                            <span id="reset-password-error" className={styles.fieldError} role="alert">{errors.password}</span>
                        )}
                    </div>

                    <div className={styles.field}>
                        <label htmlFor="reset-confirm" className={styles.srOnly}>Confirm new password</label>
                        <input
                            id="reset-confirm"
                            className={`${styles.input} ${errors.confirm ? styles.inputError : ''}`}
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Confirm new password"
                            value={confirm}
                            onChange={(e) => { setConfirm(e.target.value); setErrors(p => ({ ...p, confirm: undefined })); }}
                            autoComplete="new-password"
                            aria-invalid={errors.confirm ? 'true' : undefined}
                            aria-describedby={errors.confirm ? 'reset-confirm-error' : undefined}
                        />
                        {errors.confirm && (
                            <span id="reset-confirm-error" className={styles.fieldError} role="alert">{errors.confirm}</span>
                        )}
                    </div>

                    {errors.form && <p className={styles.errorMsg} role="alert">{errors.form}</p>}

                    <button className={styles.btnPrimary} type="submit" disabled={loading} aria-busy={loading || undefined}>
                        {loading ? <span className={styles.spinner} aria-label="Resetting" /> : 'Reset Password'}
                    </button>
                </form>
            )}
        </AuthShell>
    );
}
